import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageContext } from 'pageContext'
import { isNilOrEmpty, useAuthState } from 'packages/core'
import PersonFilter from './PersonFilter'
import ApproverPersonFilter from './ApproverPersonFilter'
import Search from './Search'
import DateTimeFilter from './DateTimeFilter'
import ChecklistFilter from './EidFilter/GenericFilter/ChecklistFilter'
import AdvancedFilter from 'components/Filters/EidFilter/AdvancedFilter'
import SubjectTypeFilter from './SubjectTypeFilter'
import AuditFilter from './AuditFilter'
import { Card, Dropdown, ToggleSwitch } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import { Box, MenuItem, Typography } from '@mui/material'
import { RiskLevelFilter } from './RiskLevelFilter'
import { FulfillmentStatusFilter } from './FulfillmentStatusFilter'
import BusinessRequestTypeFilter from './BusinessRequestTypeFilter'
import ItemTypeActionFilter from './ItemTypeActionFilter'
import ParticipantTypeFilter from './ParticipantTypeFilter'
import ApprovalDecisionFilter from './ApprovalDecisionFilter'

const PreApprovedFilter = () => {
    const { t } = useTranslation()
    const [{ showOnlyPreApproved }, dispatch] = usePageContext()
    const [toggle, setToggle] = useState(showOnlyPreApproved)

    useEffect(() => {
        if (toggle !== showOnlyPreApproved) {
            setToggle(showOnlyPreApproved)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOnlyPreApproved])

    const handleChange = () => {
        setToggle((prev) => !prev)
        dispatch({
            type: 'SET_PROP',
            payload: {
                key: 'showOnlyPreApproved',
                value: !toggle,
            },
        })
    }

    return (
        <Card
            cardTitle={t('ShowOnlyPreApproved')}
            collapsible
            expanded={showOnlyPreApproved}
        >
            <Box padding="20px">
                <ToggleSwitch
                    onChange={handleChange}
                    value={toggle}
                    label={t('ShowOnlyPreApproved')}
                />
            </Box>
        </Card>
    )
}

const WentForApprovalFilter = () => {
    const { t } = useTranslation()
    const [{ wentForApproval }, dispatch] = usePageContext()
    const [toggle, setToggle] = useState(wentForApproval)

    useEffect(() => {
        if (toggle !== wentForApproval) {
            setToggle(wentForApproval)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wentForApproval])

    const handleChange = () => {
        setToggle((prev) => !prev)
        dispatch({
            type: 'SET_PROP',
            payload: {
                key: 'wentForApproval',
                value: !toggle,
            },
        })
    }

    return (
        <Card
            cardTitle={t('WentForApproval')}
            collapsible
            expanded={wentForApproval}
        >
            <Box padding="20px">
                <ToggleSwitch
                    onChange={handleChange}
                    value={toggle}
                    label={t('WentForApproval')}
                />
            </Box>
        </Card>
    )
}

const RequestedByFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()

    return (
        <Card
            cardTitle={t('RequestedBy')}
            color="#fff"
            collapsible
            expanded={state.requestedBy !== null}
        >
            <PersonFilter
                value={state.requestedBy}
                onClear={() =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'requestedBy',
                            value: {
                                id: currentUserId,
                                friendlyName: friendlyName,
                            },
                        },
                    })
                }
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'requestedBy',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}

const ApprovedByFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()

    return (
        <Card
            cardTitle={t('Approver')}
            color="#fff"
            collapsible
            expanded={state.approver !== null}
        >
            <PersonFilter
                value={state.approver}
                onClear={() =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'approver',
                            value: {
                                id: currentUserId,
                                friendlyName: friendlyName,
                            },
                        },
                    })
                }
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'approver',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}
const ApproverFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <ApproverPersonFilter
            title={t('Approver')}
            approverType={state.approverType}
            approver={state.approver}
            handleChange={(approverType, approver) => {
                switch (approverType) {
                    case 'past':
                        dispatch({
                            type: 'SET_MULTIPLE_PROPS',
                            payload: {
                                approverType: approverType,
                                approver: approver,
                                showOnlyWhereNoApprovers: null,
                            },
                        })
                        break
                    case 'noApprover':
                        dispatch({
                            type: 'SET_MULTIPLE_PROPS',
                            payload: {
                                approverType: approverType,
                                approver: null,
                                showOnlyWhereNoApprovers: true,
                            },
                        })
                        break
                    case 'current':
                    default:
                        dispatch({
                            type: 'SET_MULTIPLE_PROPS',
                            payload: {
                                approverType: approverType,
                                approver: approver,
                                showOnlyWhereNoApprovers: null,
                            },
                        })
                }
            }}
        />
    )
}

const RequestsStatusFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('FilterByStatus')}
            collapsible
            expanded={state.status && state.status.length > 0}
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i,
                        name: i,
                        friendlyName: t(`${i}`),
                    }))
                }
                url="/api/businessRequests/statuses"
                value={state.status}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'status',
                            value: payload,
                        },
                    })
                }}
                contextProp="status"
                selectedValueIndicatorProp="status"
            />
        </Card>
    )
}

const RequestItemsStatusFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('FilterByStatus')}
            collapsible
            expanded={state.status && state.status.length > 0}
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i,
                        name: i,
                        friendlyName: t(`${i}`),
                    }))
                }
                url="/api/businessRequestItems/statuses"
                value={state.status}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'status',
                            value: payload,
                        },
                    })
                }}
                contextProp="status"
                selectedValueIndicatorProp="status"
            />
        </Card>
    )
}

const BusinessProcessStatusFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('FilterByStatus')}
            collapsible
            expanded={state.status && state.status.length > 0}
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i.id,
                        name: i.name,
                        friendlyName: i.friendlyName,
                    }))
                }
                url="/api/WorkflowTasks/statuses"
                value={state.status}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'status',
                            value: payload,
                        },
                    })
                }}
                contextProp="status"
                selectedValueIndicatorProp="status"
            />
        </Card>
    )
}

const ApproverDecisionsItemStatusFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('ItemStatus')}
            collapsible
            expanded={
                state.approverDecisionItemStatus &&
                state.approverDecisionItemStatus.length > 0
            }
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i.name,
                        name: i.name,
                        friendlyName: t(`${i.name}`),
                    }))
                }
                url="api/Lookup/businessRequestItemStatuses"
                value={state.approverDecisionItemStatus}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'approverDecisionItemStatus',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}

const ApproverDecisionsStepStatusFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('StepStatus')}
            collapsible
            expanded={
                state.approverDecisionStepStatus &&
                state.approverDecisionStepStatus.length > 0
            }
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i.name,
                        name: i.name,
                        friendlyName: t(`${i.name}`),
                    }))
                }
                url="api/Lookup/approvalStepStatuses"
                value={state.approverDecisionStepStatus}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'approverDecisionStepStatus',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}

const ApproverDecisionsRiskTypesFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('RiskType')}
            collapsible
            expanded={
                state.approverDecisionRiskType &&
                state.approverDecisionRiskType.length > 0
            }
        >
            <ChecklistFilter
                transformResponse={(data) =>
                    data.map((i) => ({
                        id: i.name,
                        name: i.name,
                        friendlyName: t(`${i.name}`),
                    }))
                }
                url="api/Lookup/riskTypes"
                value={state.approverDecisionRiskType}
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'approverDecisionRiskType',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}

const RequestItemTypesFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('FilterByItemType')}
            collapsible
            expanded={state.itemType && state.itemType.length > 0}
        >
            <Box
                style={{
                    paddingRight: '10px',
                }}
            >
                <ChecklistFilter
                    transformResponse={(data) =>
                        data
                            .map((i) => ({
                                id: i.id,
                                name: i.name,
                                friendlyName: i.localizedFriendlyName,
                            }))
                            ?.sort((a, b) =>
                                a.friendlyName.localeCompare(b.friendlyName),
                            )
                    }
                    url="/api/businessRequestItems/itemTypes"
                    value={state.itemType}
                    onChange={(payload) => {
                        dispatch({
                            type: 'SET_PROP',
                            payload: {
                                key: 'itemType',
                                value: payload,
                            },
                        })
                    }}
                    contextProp="itemType"
                    selectedValueIndicatorProp="itemType"
                    searchable={true}
                    searchPlaceholder={t('SearchItemType')}
                    supportsServerSideSearch={false}
                />
            </Box>
        </Card>
    )
}

const RiskLevelFilterCard = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('FilterByRiskLevel')}
            collapsible
            expanded={state.riskLevels && state.riskLevels.length > 0}
        >
            <RiskLevelFilter
                value={state.riskLevels}
                onChange={(values) =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'riskLevels',
                            value: values,
                        },
                    })
                }
            />
        </Card>
    )
}

const ApproverDecisionRiskLevelsFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <Card
            cardTitle={t('RiskLevel')}
            collapsible
            expanded={state.riskLevels && state.riskLevels.length > 0}
        >
            <RiskLevelFilter
                transformData={(data) =>
                    [...data].reverse().map((i) => {
                        return {
                            value: i.name,
                            label: t(`${i.name}Risk`),
                        }
                    })
                }
                isApiSupported={true}
                value={state.riskLevels}
                onChange={(values) =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'riskLevels',
                            value: values,
                        },
                    })
                }
            />
        </Card>
    )
}

const TextSearchFilter = () => {
    const [state, dispatch] = usePageContext()

    return (
        <Search
            handleSearch={(searchKey) =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'searchTerm',
                        value: searchKey,
                    },
                })
            }
            handleClear={(searchKey) =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'searchTerm',
                        value: searchKey,
                    },
                })
            }
            value={state.searchTerm}
        />
    )
}

export const StartDateFilter = ({ label = 'BetweenDate' }) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t(label)}
            value={
                isNilOrEmpty(state.startDate) ? null : state.startDate.local()
            }
            onChange={(date) => {
                const dateToSet = date
                dateToSet.startOf('day')
                dateToSet.utc()
                if (
                    state.endDate &&
                    new Date(dateToSet) > new Date(state.endDate)
                ) {
                    dispatch({
                        type: 'SET_MULTIPLE_PROPS',
                        payload: {
                            startDate: dateToSet,
                            endDate: null,
                        },
                    })
                } else {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'startDate',
                            value: dateToSet,
                        },
                    })
                }
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'startDate',
                        value: null,
                    },
                })
            }
        />
    )
}

export const EndDateFilter = ({ label = 'AndDate' }) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t(label)}
            minDate={state.startDate ? state.startDate : null}
            value={isNilOrEmpty(state.endDate) ? null : state.endDate.local()}
            onChange={(date) => {
                const dateToSet = date.endOf('day').subtract(1, 'minutes')
                dateToSet.utc()

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: dateToSet,
                    },
                })
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: null,
                    },
                })
            }
        />
    )
}

const dateFilterStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '15px',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    border: 'solid 1px #ebebed',
    borderRadius: '5px',
    backgroundColor: '#ffffff',
    color: '#7D7C7C',
    height: '40px',
    minWidth: '560px',
}

const DateFilter = ({ label, options = [] }) => {
    const [state, dispatch] = usePageContext()
    const [anchorEl, setAnchorEl] = useState(null)
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const defaultOption = options.filter((o) => o.default)[0]
    const [selectedValue, setSelectedValue] = useState(defaultOption)
    useEffect(() => {
        if (selectedValue && state.dateFilterType !== selectedValue.enumValue) {
            dispatch({
                type: 'SET_MULTIPLE_PROPS',
                payload: {
                    dateFilterType: selectedValue.enumValue,
                },
            })
        }
    }, [selectedValue])
    const handleClose = () => {
        setAnchorEl(null)
        setOpen(false)
    }
    const styles = {
        rootMenu: {
            paddingRight: '0px',
            marginTop: '15px',
            width: '270px',
        },
        list: {
            padding: '0px 6px',
        },
    }

    return options.length > 0 ? (
        <Box style={dateFilterStyles}>
            <Box
                onClick={(e) => {
                    setOpen((prev) => !prev)
                    setAnchorEl(e.currentTarget)
                }}
                style={{
                    minWidth: '220px',
                    display: 'flex',
                    cursor: 'pointer',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    style={{
                        color: '#000',
                        fontSize: '15px',
                        marginTop: '2px',
                    }}
                >
                    {t(selectedValue?.label)}
                </Typography>
                <Icon
                    name="Arrow"
                    color="#307fc1"
                    width="26"
                    height="26"
                    style={{ marginRight: '10px' }}
                />
            </Box>
            <Dropdown
                disableScrollLock
                style={{ position: 'absolute', inset: '0px', zIndex: 2 }}
                width={'auto'}
                showCone={true}
                open={open}
                anchorEl={anchorEl}
                rootStyles={styles.rootMenu}
                listStyles={styles.list}
                handleClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                iconPosition={{
                    top: '-5px',
                    right: '10px',
                }}
            >
                {options.map((option, i) => (
                    <MenuItem
                        key={option.value + i}
                        onClick={() => {
                            setOpen(false)
                            setSelectedValue(option)
                        }}
                    >
                        <Typography>{t(option.label)}</Typography>
                    </MenuItem>
                ))}
            </Dropdown>
            <StartDateFilter />
            <EndDateFilter />
        </Box>
    ) : (
        <Box style={dateFilterStyles}>
            <Box minWidth="120px">{label}</Box>
            <StartDateFilter />
            <EndDateFilter />
        </Box>
    )
}

const requestsAdvancedSearchForms = {
    colorCode: {
        primary: '#2920ad',
        secondary: '#ebeaf7',
    },
    filters: [
        {
            name: 'description',
            type: 'text',
            label: 'Name',
            placeholder: 'Name',
            contextProp: 'advancedSearch.forms.description',
        },
        {
            name: 'requestNumber',
            type: 'text',
            label: 'BusinessRequestNumber',
            placeholder: 'BusinessRequestNumber',
            contextProp: 'advancedSearch.forms.requestNumber',
        },
    ],
}

const RequestsAdvancedSearchFilters = () => {
    const { t } = useTranslation()
    const [state] = usePageContext()
    return (
        <Card
            cardTitle={t('AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
            expanded={
                state['advancedSearch.forms.description'] ||
                state['advancedSearch.forms.requestNumber']
            }
        >
            <AdvancedFilter forms={requestsAdvancedSearchForms} tags={null} />
        </Card>
    )
}
const InitiatedByFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()

    return (
        <Card
            cardTitle={t('Initiator')}
            color="#fff"
            collapsible
            expanded={state.initiatedBy !== null}
        >
            <PersonFilter
                value={state.initiatedBy}
                onClear={() =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'initiatedBy',
                            value: {
                                id: currentUserId,
                                friendlyName: friendlyName,
                            },
                        },
                    })
                }
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'initiatedBy',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}
const AccessAssigneeFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()

    return (
        <Card
            cardTitle={t('AccessAssignee')}
            color="#fff"
            collapsible
            expanded={state.accessAssignee !== null}
        >
            <PersonFilter
                value={state.accessAssignee}
                onClear={() =>
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'accessAssignee',
                            value: {
                                id: currentUserId,
                                friendlyName: friendlyName,
                            },
                        },
                    })
                }
                onChange={(payload) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: {
                            key: 'accessAssignee',
                            value: payload,
                        },
                    })
                }}
            />
        </Card>
    )
}
const requestItemsAdvancedSearchForms = {
    colorCode: {
        primary: '#2920ad',
        secondary: '#ebeaf7',
    },
    filters: [
        {
            name: 'name',
            type: 'text',
            label: 'FriendlyName',
            placeholder: 'FriendlyName',
            contextProp: 'advancedSearch.forms.friendlyName',
        },
        {
            name: 'requestNumber',
            type: 'text',
            label: 'BusinessRequestNumber',
            placeholder: 'BusinessRequestNumber',
            contextProp: 'advancedSearch.forms.requestNumber',
        },
    ],
}

const RequestItemsAdvancedSearchFilters = () => {
    const { t } = useTranslation()

    return (
        <Card
            cardTitle={t('AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
        >
            <AdvancedFilter
                forms={requestItemsAdvancedSearchForms}
                tags={null}
            />
        </Card>
    )
}

const workflowssAdvancedSearchForms = {
    colorCode: {
        primary: '#2920ad',
        secondary: '#ebeaf7',
    },
    filters: [
        {
            name: 'name',
            type: 'text',
            label: 'Task',
            placeholder: 'Task',
            contextProp: 'advancedSearch.forms.name',
        },
        {
            name: 'Name',
            type: 'text',
            label: 'WorkflowDisplayName',
            placeholder: 'WorkflowDisplayName',
            contextProp: 'advancedSearch.forms.workflowDisplayName',
        },
        {
            name: 'id',
            type: 'text',
            label: 'BusinessRequestNumber',
            placeholder: 'BusinessRequestNumber',
            contextProp: 'advancedSearch.forms.id',
        },
    ],
}
const WorkflowsAdvancedSearchFilters = () => {
    const { t } = useTranslation()

    return (
        <Card
            cardTitle={t('AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
        >
            <AdvancedFilter forms={workflowssAdvancedSearchForms} tags={null} />
        </Card>
    )
}

const itemApproverDecisionsAdvancedSearchForms = {
    colorCode: {
        primary: '#2920ad',
        secondary: '#ebeaf7',
    },
    filters: [
        {
            name: 'name',
            type: 'text',
            label: 'Name',
            placeholder: 'Name',
            contextProp: 'advancedSearch.forms.friendlyName',
        },
        {
            name: 'requestName',
            type: 'text',
            label: 'BusinessRequestName',
            placeholder: 'BusinessRequestName',
            contextProp: 'advancedSearch.forms.requestName',
        },
        {
            name: 'requestNumber',
            type: 'text',
            label: 'BusinessRequestNumber',
            placeholder: 'BusinessRequestNumber',
            contextProp: 'advancedSearch.forms.requestNumber',
        },
    ],
}

const ItemApproverDecisionsAdvancedSearchFilters = () => {
    const { t } = useTranslation()

    return (
        <Card
            cardTitle={t('AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
        >
            <AdvancedFilter
                forms={itemApproverDecisionsAdvancedSearchForms}
                tags={null}
            />
        </Card>
    )
}

const Filters = () => <></>
Filters.BusinessRequestType = BusinessRequestTypeFilter
Filters.RequestedBy = RequestedByFilter
Filters.ApprovedBy = ApprovedByFilter
Filters.PreApproved = PreApprovedFilter
Filters.Approver = ApproverFilter
Filters.RequestsStatus = RequestsStatusFilter
Filters.BusinessRequestItemsStatus = RequestItemsStatusFilter
Filters.BusinessRequestItemTypes = RequestItemTypesFilter
Filters.TextSearch = TextSearchFilter
Filters.StartDate = StartDateFilter
Filters.EndDate = EndDateFilter
Filters.DateFilter = DateFilter
Filters.RequestsAdvancedSearch = RequestsAdvancedSearchFilters
Filters.RequestItemsAdvancedSearch = RequestItemsAdvancedSearchFilters
Filters.WorkflowsAdvancedSearch = WorkflowsAdvancedSearchFilters
Filters.ItemApproverDecisionsAdvancedSearchFilters =
    ItemApproverDecisionsAdvancedSearchFilters
Filters.SubjectType = SubjectTypeFilter
Filters.Audit = AuditFilter
Filters.ItemTypeAction = ItemTypeActionFilter
Filters.ParticipantType = ParticipantTypeFilter
Filters.ApprovalDecisionFilter = ApprovalDecisionFilter
Filters.RiskLevelFilter = RiskLevelFilterCard
Filters.FulfillmentStatusFilter = FulfillmentStatusFilter
Filters.BusinessProcessStatusFilter = BusinessProcessStatusFilter
Filters.ApproverDecisionsItemStatusFilter = ApproverDecisionsItemStatusFilter
Filters.ApproverDecisionsStepStatusFilter = ApproverDecisionsStepStatusFilter
Filters.ApproverDecisionsRiskTypesFilter = ApproverDecisionsRiskTypesFilter
Filters.ApproverDecisionRiskLevelsFilter = ApproverDecisionRiskLevelsFilter

const filtersMap = {
    businessRequestType: BusinessRequestTypeFilter,
    subjectType: SubjectTypeFilter,
    audit: AuditFilter,
    itemTypeAction: ItemTypeActionFilter,
    participantType: ParticipantTypeFilter,
    approvalDecision: ApprovalDecisionFilter,
    requestedBy: RequestedByFilter,
    approvedBy: ApprovedByFilter,
    approver: ApproverFilter,
    preApproved: PreApprovedFilter,
    wentForApproval: WentForApprovalFilter,
    requestStatus: RequestsStatusFilter,
    requestItemStatus: RequestItemsStatusFilter,
    businessProcessStatus: BusinessProcessStatusFilter,
    approverDecisionItemStatus: ApproverDecisionsItemStatusFilter,
    approverDecisionStepStatus: ApproverDecisionsStepStatusFilter,
    approverDecisionRiskType: ApproverDecisionsRiskTypesFilter,
    approverDecisionRiskLevels: ApproverDecisionRiskLevelsFilter,
    itemType: RequestItemTypesFilter,
    riskLevel: RiskLevelFilterCard,
    requestsAdvancedSearch: RequestsAdvancedSearchFilters,
    requestItemsAdvancedSearch: RequestItemsAdvancedSearchFilters,
    workflowsAdvancedSearch: WorkflowsAdvancedSearchFilters,
    itemApproverDecisionsAdvancedSearch:
        ItemApproverDecisionsAdvancedSearchFilters,
    fulfillmentStatus: FulfillmentStatusFilter,
    initiatedBy: InitiatedByFilter,
    accessAssignee: AccessAssigneeFilter,
}

Filters.filtersMap = filtersMap

export default Filters
